// The Variables window's columns, persisted view settings (spec 6.10.17), filters, sorting and grouping
// (spec 6.10.9).
import { formatRelative } from '../lib/format';
import { strings } from '../strings';
import { type RowStatus, type StatusId, STATUS_ORDER, type VariableApp, type VariableRow } from './model';
import { locationEntry } from './registry';

export type ColumnId =
  | 'select'
  | 'variable'
  | 'source'
  | 'nameInSource'
  | 'value'
  | 'status'
  | 'resource'
  | 'lastChanged'
  | 'version'
  | 'usedBy'
  | 'delivery'
  | 'rotation'
  | 'actions';

/** Columns that can be shown, hidden and moved; `select`, `variable` and `actions` are fixed. */
export const OPTIONAL_COLUMNS: readonly ColumnId[] = [
  'source',
  'nameInSource',
  'value',
  'status',
  'resource',
  'lastChanged',
  'version',
  'usedBy',
  'delivery',
  'rotation',
];
export const DEFAULT_COLUMNS: readonly ColumnId[] = ['source', 'nameInSource', 'value', 'status'];

export type GroupBy = 'resource' | 'source' | 'none';
export type SortDirection = 'ascending' | 'descending';

export interface ViewSettings {
  view: 'variables' | 'secrets';
  /** Visible optional columns. */
  columns: ColumnId[];
  /** Every optional column in display order. */
  order: ColumnId[];
  /** Null: the default (by resource with more than one app). */
  group: GroupBy | null;
  sort: { column: ColumnId; direction: SortDirection } | null;
}

export const DEFAULT_VIEW: ViewSettings = {
  view: 'variables',
  columns: [...DEFAULT_COLUMNS],
  order: [...OPTIONAL_COLUMNS],
  group: null,
  sort: null,
};

const isColumn = (value: unknown): value is ColumnId =>
  typeof value === 'string' && (OPTIONAL_COLUMNS as readonly string[]).includes(value);

/** Reads stored view settings member by member; unknown members and values are dropped. */
export function sanitizeView(raw: unknown): ViewSettings {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return { ...DEFAULT_VIEW };
  const value = raw as Record<string, unknown>;
  const view = value.view === 'secrets' ? 'secrets' : 'variables';
  const columns = Array.isArray(value.columns)
    ? [...new Set(value.columns.filter(isColumn))]
    : [...DEFAULT_COLUMNS];
  const stored = Array.isArray(value.order) ? [...new Set(value.order.filter(isColumn))] : [];
  const order = [...stored, ...OPTIONAL_COLUMNS.filter((c) => !stored.includes(c))];
  const group =
    value.group === 'resource' || value.group === 'source' || value.group === 'none' ? value.group : null;
  let sort: ViewSettings['sort'] = null;
  const rawSort = value.sort as Record<string, unknown> | null | undefined;
  if (
    rawSort &&
    typeof rawSort === 'object' &&
    (isColumn(rawSort.column) || rawSort.column === 'variable') &&
    (rawSort.direction === 'ascending' || rawSort.direction === 'descending')
  )
    sort = { column: rawSort.column as ColumnId, direction: rawSort.direction };
  return { view, columns, order, group, sort };
}

/**
 * The columns to draw, in order: selection, Variable, the visible optional ones, Actions. Rotation is offered only when
 * a row in scope has rotation (feature spec 15 19.5), so it never shows on Azure.
 */
export function visibleColumns(
  settings: ViewSettings,
  options: { select: boolean; rotation?: boolean },
): ColumnId[] {
  return [
    ...(options.select ? (['select'] as const) : []),
    'variable',
    ...settings.order.filter((c) => settings.columns.includes(c) && (c !== 'rotation' || !!options.rotation)),
    'actions',
  ];
}

/** The optional columns Columns ▾ offers. */
export function offeredColumns(options: { rotation: boolean }): ColumnId[] {
  return OPTIONAL_COLUMNS.filter((c) => c !== 'rotation' || options.rotation);
}

/** Moves an optional column one place left or right in the display order (Alt+arrows on a header). */
export function moveColumn(settings: ViewSettings, column: ColumnId, delta: -1 | 1): ViewSettings {
  const visible = settings.order.filter((c) => settings.columns.includes(c));
  const i = visible.indexOf(column);
  const j = i + delta;
  if (i < 0 || j < 0 || j >= visible.length) return settings;
  const swapped = [...visible];
  [swapped[i], swapped[j]] = [swapped[j], swapped[i]];
  const hidden = settings.order.filter((c) => !settings.columns.includes(c));
  return { ...settings, order: [...swapped, ...hidden] };
}

export function effectiveGroup(settings: ViewSettings, appCount: number): GroupBy {
  return settings.group ?? (appCount > 1 ? 'resource' : 'none');
}

// ---------------------------------------------------------------- filters

export type KindFilter = 'declared' | 'system' | 'generated' | 'not-secret';

export interface Filters {
  search: string;
  /** A location type. */
  source: string | null;
  kind: KindFilter | null;
  /** `issues`: rows whose worst status is danger or warning. */
  status: 'issues' | StatusId | null;
  resource: string | null;
  /** Run page: only the resources of the run. */
  inRun: boolean;
}

export const NO_FILTERS: Filters = {
  search: '',
  source: null,
  kind: null,
  status: null,
  resource: null,
  inRun: false,
};

export function hasFilters(filters: Filters): boolean {
  return !!(filters.search.trim() || filters.source || filters.kind || filters.status || filters.resource);
}

/** What search looks at: the name, name in source, source label and non-secret values (never secrets). */
export function searchText(row: VariableRow): string {
  const parts = [
    row.name,
    row.displayName,
    row.externalTrigger?.name ?? '',
    row.location.name ?? '',
    locationEntry(row.location).label(row.location),
  ];
  if (!row.secret) parts.push(row.resolvedValue ?? '', row.value ?? '', row.design?.resolvedValue ?? '');
  return parts.join(' ').toLocaleLowerCase();
}

export function filterRows(
  rows: readonly VariableRow[],
  statuses: ReadonlyMap<string, readonly RowStatus[]>,
  filters: Filters,
  runResources: ReadonlySet<string> | null,
): VariableRow[] {
  const words = filters.search.toLocaleLowerCase().split(/\s+/).filter(Boolean);
  return rows.filter((row) => {
    if (filters.inRun && runResources && !runResources.has(row.resourceId)) return false;
    if (filters.resource && row.resourceId !== filters.resource) return false;
    if (filters.source && row.location.type !== filters.source) return false;
    if (filters.kind) {
      if (filters.kind === 'not-secret' ? row.secret : row.kind !== filters.kind || !row.secret) return false;
    }
    if (filters.status) {
      const list = statuses.get(row.id) ?? [];
      if (filters.status === 'issues') {
        const tone = list[0]?.tone;
        if (tone !== 'danger' && tone !== 'warning') return false;
      } else if (!list.some((s) => s.id === filters.status)) return false;
    }
    if (words.length) {
      const text = searchText(row);
      if (!words.every((w) => text.includes(w))) return false;
    }
    return true;
  });
}

// ---------------------------------------------------------------- sorting and grouping

function sortKey(
  row: VariableRow,
  column: ColumnId,
  statuses: ReadonlyMap<string, readonly RowStatus[]>,
): string | number {
  switch (column) {
    case 'variable':
      return row.displayName.toLocaleLowerCase();
    case 'source':
      return locationEntry(row.location).label(row.location).toLocaleLowerCase();
    case 'nameInSource':
      return (row.location.name ?? '').toLocaleLowerCase();
    case 'value':
      return row.secret ? '' : (row.resolvedValue ?? row.value ?? '').toLocaleLowerCase();
    case 'status': {
      const first = statuses.get(row.id)?.[0];
      return first ? STATUS_ORDER.indexOf(first.id) : STATUS_ORDER.length;
    }
    case 'resource':
      return row.resourceId;
    case 'lastChanged':
      return row.metadata?.updatedAt ?? '';
    case 'version':
      return row.metadata?.currentVersion ?? '';
    case 'usedBy':
      return row.metadata?.usedBy.length ?? 0;
    case 'delivery':
      return row.delivery?.label ?? '';
    default:
      return 0;
  }
}

/** Sorts by a column (stable: the server order breaks ties), or keeps the server order. */
export function sortRows(
  rows: readonly VariableRow[],
  sort: ViewSettings['sort'],
  statuses: ReadonlyMap<string, readonly RowStatus[]>,
): VariableRow[] {
  if (!sort) return [...rows];
  const factor = sort.direction === 'ascending' ? 1 : -1;
  return rows
    .map((row, i) => ({ row, i, key: sortKey(row, sort.column, statuses) }))
    .sort((a, b) => (a.key < b.key ? -factor : a.key > b.key ? factor : a.i - b.i))
    .map(({ row }) => row);
}

/** The next sort of a header click: ascending, descending, then the server order. */
export function nextSort(sort: ViewSettings['sort'], column: ColumnId): ViewSettings['sort'] {
  if (sort?.column !== column) return { column, direction: 'ascending' };
  if (sort.direction === 'ascending') return { column, direction: 'descending' };
  return null;
}

export type Line =
  | {
      kind: 'group';
      key: string;
      label: string;
      /** For resource groups. */
      app: VariableApp | null;
      rows: VariableRow[];
      expanded: boolean;
    }
  | { kind: 'row'; row: VariableRow; level: 1 | 2 };

/** The table's lines: group rows (when grouped) followed by their variables unless collapsed. */
export function groupLines(
  rows: readonly VariableRow[],
  group: GroupBy,
  apps: readonly VariableApp[],
  collapsed: ReadonlySet<string>,
): Line[] {
  if (group === 'none') return rows.map((row) => ({ kind: 'row', row, level: 1 }));
  const groups = new Map<string, { label: string; app: VariableApp | null; rows: VariableRow[] }>();
  if (group === 'resource') {
    for (const app of apps) groups.set(app.resourceId, { label: app.resourceId, app, rows: [] });
  }
  for (const row of rows) {
    const key = group === 'resource' ? row.resourceId : row.location.type;
    let entry = groups.get(key);
    if (!entry) {
      entry = {
        label: group === 'resource' ? row.resourceId : locationEntry(row.location).label(row.location),
        app: null,
        rows: [],
      };
      groups.set(key, entry);
    }
    entry.rows.push(row);
  }
  const lines: Line[] = [];
  for (const [key, entry] of groups) {
    if (group === 'source' && entry.rows.length === 0) continue;
    const expanded = !collapsed.has(key);
    lines.push({ kind: 'group', key, label: entry.label, app: entry.app, rows: entry.rows, expanded });
    if (expanded) for (const row of entry.rows) lines.push({ kind: 'row', row, level: 2 });
  }
  return lines;
}

/** "2 h ago by ana", for the Last changed column and the details pane. */
export function changedText(row: VariableRow, now = Date.now()): string {
  const m = row.metadata;
  if (!row.secret || !m?.updatedAt) return '';
  return `${formatRelative(m.updatedAt, now)} ${m.updatedBy ? strings.common.by(m.updatedBy) : `· ${strings.variables.value.outsideNebisys}`}`;
}
